import type { ReactNode } from "react";
import { Redirect, Route, Switch } from "wouter";
import { AccountPage } from "./account/Account";
import { AdminPage } from "./admin/Admin";
import { MagicLinkPage, SignInPage } from "./auth/SignIn";
import { landingFor, useSession } from "./auth/session";
import { EmptyState, PageHeader, PageShell } from "./company-ui";
import { HelpArticlePage, HelpCenterPage } from "./help/HelpCenter";
import { AppShell } from "./shell/AppShell";
import { NAV_ITEMS } from "./shell/nav";
import { ComplianceNotYetPage, NotYetPage } from "./shell/pages";
import { BuilderPage } from "./builder/Builder";
import { ProcessPage } from "./process/Process";
import { ProgramsPage } from "./programs/Programs";
import { WipPage } from "./wip/Wip";
import { RatesPage } from "./rates/Rates";
import { EstimatePage } from "./estimate/Estimate";
import { RespondPage } from "./estimate/Respond";
import { TemplatesPage } from "./templates/Templates";

/**
 * Routes. Sign-in is ALN-23, Administration and Your account ALN-24, the shell ALN-25, the help
 * center ALN-53, Programs and the Program builder ALN-34, Rates and pools ALN-33, the Process editor ALN-32. Every nav destination has a route; screens not built yet show NotYetPage until
 * their entry lands.
 */
export function App() {
  return (
    <Switch>
      <Route path="/sign-in" component={SignInPage} />
      <Route path="/sign-in/link" component={MagicLinkPage} />
      <Route path="/"><SignedIn><Landing /></SignedIn></Route>
      <Route path="/survey" component={RespondPage} />
      <Route path="/estimate"><SignedIn><AppShell><EstimatePage /></AppShell></SignedIn></Route>
      <Route path="/admin/:tab?"><SignedIn><AppShell><AdminPage /></AppShell></SignedIn></Route>
      <Route path="/account"><SignedIn><AppShell><AccountPage /></AppShell></SignedIn></Route>
      <Route path="/help"><SignedIn><AppShell><HelpCenterPage /></AppShell></SignedIn></Route>
      <Route path="/programs"><SignedIn><AppShell><ProgramsPage /></AppShell></SignedIn></Route>
      <Route path="/wip"><SignedIn><AppShell><WipPage /></AppShell></SignedIn></Route>
      <Route path="/templates"><SignedIn><AppShell><TemplatesPage /></AppShell></SignedIn></Route>
      <Route path="/rates"><SignedIn><AppShell><RatesPage /></AppShell></SignedIn></Route>
      <Route path="/builder"><SignedIn><AppShell><BuilderPage /></AppShell></SignedIn></Route>
      <Route path="/process"><SignedIn><AppShell><ProcessPage /></AppShell></SignedIn></Route>
      <Route path="/help/:id">{p => <SignedIn><AppShell><HelpArticlePage id={p.id} /></AppShell></SignedIn>}</Route>
      <Route path="/compliance"><SignedIn><AppShell><ComplianceNotYetPage /></AppShell></SignedIn></Route>
      {NAV_ITEMS.filter(i => i.entry && !i.tab).map(i => (
        <Route key={i.path} path={i.path}><SignedIn><AppShell><NotYetPage item={i} /></AppShell></SignedIn></Route>
      ))}
      <Route><NotFound /></Route>
    </Switch>
  );
}

/** Where sign-in lands: Programs, or Team estimates for a person who only answers surveys (estimate.md A). */
function Landing() {
  const { data } = useSession();
  return <Redirect to={landingFor(data)} />;
}

/** Pages behind sign-in: anyone without a finished session goes to /sign-in. */
function SignedIn({ children }: { children: ReactNode }) {
  const session = useSession();
  if (session.isPending) return <main className="auth" aria-busy="true" />;
  if (!session.data || session.data.stage !== "active") return <Redirect to="/sign-in" replace />;
  return <>{children}</>;
}

function NotFound() {
  return (
    <main>
      <PageShell maxWidth="lg">
        <PageHeader title="Page not found" />
        <EmptyState title="There's nothing at this address" action={<a className="btn" href="/">Go to Avero Vintner</a>} />
      </PageShell>
    </main>
  );
}
